<template>
  <div class="mx-auto w-full max-w-6xl space-y-14 px-4 pb-16 pt-6 lg:px-8">
    <section>
      <div :class="sectionHeadClass">
        <h2 :class="sectionTitleClass">{{ t("pages.hardsubToSubtitle.landing.title") }}</h2>
        <p class="text-pretty text-[15px] text-muted-foreground">
          {{ t("pages.hardsubToSubtitle.landing.subtitle") }}
        </p>
      </div>

      <!-- Sample output stays left-to-right in every locale -->
      <figure>
        <div class="grid gap-4 md:grid-cols-2" dir="ltr">
          <div class="relative aspect-video overflow-hidden rounded-lg border bg-scrim">
            <div
              class="absolute inset-x-[6%] bottom-[9%] rounded-sm border-2 border-dashed border-primary px-2 py-1.5 text-center text-sm font-semibold text-scrim-foreground sm:text-base"
            >
              {{ sampleCues[0].text }}
            </div>
          </div>

          <div class="rounded-lg border bg-card p-4 shadow-sm">
            <p class="mb-3 font-mono text-xs text-subtle-foreground">sample-episode.en.srt</p>
            <div class="space-y-3 font-mono text-[13px] leading-relaxed">
              <p v-for="(cue, index) in sampleCues" :key="cue.time">
                <span class="block text-subtle-foreground">{{ index + 1 }}</span>
                <span class="block text-subtle-foreground">{{ cue.time }}</span>
                <span class="block">{{ cue.text }}</span>
              </p>
            </div>
          </div>
        </div>
        <figcaption class="mt-3 text-center text-[13px] text-subtle-foreground">
          {{ t("pages.hardsubToSubtitle.landing.sampleCaption") }}
        </figcaption>
      </figure>
    </section>

    <section>
      <div :class="sectionHeadClass">
        <h2 :class="sectionTitleClass">{{ t("pages.hardsubToSubtitle.landing.howTitle") }}</h2>
      </div>
      <ol class="grid gap-4 md:grid-cols-3">
        <li v-for="n in 3" :key="`step-${n}`" :class="cardClass">
          <div class="mb-2 font-mono text-sm text-subtle-foreground">{{ n }}</div>
          <h3 :class="cardTitleClass">{{ t(`pages.hardsubToSubtitle.landing.step${n}Title`) }}</h3>
          <p :class="cardBodyClass">{{ t(`pages.hardsubToSubtitle.landing.step${n}Desc`) }}</p>
        </li>
      </ol>
    </section>

    <section>
      <div :class="sectionHeadClass">
        <h2 :class="sectionTitleClass">{{ t("pages.hardsubToSubtitle.landing.featuresTitle") }}</h2>
      </div>
      <div class="grid gap-4 sm:grid-cols-2">
        <div v-for="feature in features" :key="feature.key" :class="cardClass">
          <Icon :name="feature.icon" class="mb-2 h-5 w-5 text-muted-foreground" />
          <h3 :class="cardTitleClass">
            {{ t(`pages.hardsubToSubtitle.landing.${feature.key}Title`) }}
          </h3>
          <p :class="cardBodyClass">{{ t(`pages.hardsubToSubtitle.landing.${feature.key}Desc`) }}</p>
        </div>
      </div>
    </section>

    <section>
      <div :class="sectionHeadClass">
        <h2 :class="sectionTitleClass">{{ t("pages.hardsubToSubtitle.landing.faqTitle") }}</h2>
      </div>
      <div class="mx-auto max-w-3xl space-y-3">
        <details v-for="item in faq" :key="item.question" class="rounded-lg border bg-card p-4">
          <summary class="cursor-pointer text-[14.5px] font-semibold tracking-tight">
            {{ item.question }}
          </summary>
          <p :class="[cardBodyClass, 'mt-2']">{{ item.answer }}</p>
        </details>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { SUBTITLE_CREDITS_PER_MINUTE } from "~/constants/credits";
import { HARDSUB_MAX_SECONDS } from "~/utils/hardsub-region";

const { t } = useI18n();

const sectionHeadClass = "mx-auto mb-6 max-w-[640px] space-y-2 text-center";
const sectionTitleClass = "text-balance text-2xl font-bold tracking-tight";
const cardClass = "rounded-lg border bg-card p-4 shadow-sm md:p-5";
const cardTitleClass = "mb-1 text-[14.5px] font-semibold tracking-tight";
const cardBodyClass = "text-sm text-muted-foreground";

// Synthetic demonstration output, see the "sample-" labelling rule in DESIGN.md.
const sampleCues = [
  { time: "00:00:12,400 --> 00:00:15,100", text: "Meet me at the station at dawn." },
  { time: "00:00:15,600 --> 00:00:18,200", text: "Bring only what you can carry." },
];

const features = [
  { key: "feature1", icon: "lucide:languages" },
  { key: "feature2", icon: "lucide:timer" },
  { key: "feature3", icon: "lucide:scan-text" },
  { key: "feature4", icon: "lucide:globe" },
];

const faqParams = {
  credits: SUBTITLE_CREDITS_PER_MINUTE,
  hours: HARDSUB_MAX_SECONDS / 3600,
};
const faq = computed(() =>
  [1, 2, 3, 4, 5, 6].map((n) => ({
    question: t(`pages.hardsubToSubtitle.landing.faq${n}Question`),
    answer: t(`pages.hardsubToSubtitle.landing.faq${n}Answer`, faqParams),
  }))
);

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: computed(() =>
        JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.value.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        })
      ),
    },
  ],
});
</script>
